import React, { useCallback, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import {
  __experimentalFormField as FormField,
  __experimentalFormFieldProps as FormFieldProps,
  __experimentalAutocompleteInput,
  withChangeableValue,
  withErrorTooltip,
} from '@cp/ds/src/components/form';
import { ListItemText } from '@cp/ds/src/components/menuList';
import { IAddressOption, useAddressAutocomplete, useLocationAutocomplete } from '../model';

const AutocompleteInput = withErrorTooltip(withChangeableValue(__experimentalAutocompleteInput));

export interface AddressFieldProps extends Partial<FormFieldProps> {
  onOptionsUpdate?: (suggestions: IAddressOption[]) => void;
  autocompleteProvider: (q: string) => Promise<IAddressOption[]>;
  renderOption?: (o: IAddressOption) => React.ReactNode;
  selectOnFocus?: boolean;
}
export const AddressField: React.FC<AddressFieldProps> = ({
  name = 'address',
  onOptionsUpdate,
  renderOption,
  autocompleteProvider,
  selectOnFocus = false,
  ...props
}) => {
  const [inProgress, setInProgress] = useState(false);
  const getOptions = useCallback(
    async (query: string) => {
      setInProgress(true);
      return autocompleteProvider(query)
        .then((o) => {
          onOptionsUpdate?.(o);
          return o;
        })
        .finally(() => setInProgress(false));
    },
    [autocompleteProvider, onOptionsUpdate],
  );
  const selectContent = useCallback((event: React.SyntheticEvent<HTMLInputElement>) => {
    if (selectOnFocus) {
      event.currentTarget.setSelectionRange(0, event.currentTarget.value.length);
    }
  }, [selectOnFocus]);
  return (
    <FormField
      name={name}
      {...props}
      render={({ size: _size, ...controlProps }) => (
        <AutocompleteInput
          {...controlProps}
          endAdornment={inProgress ? <CircularProgress color="inherit" size={20} /> : null}
          getOptions={getOptions}
          onClick={selectContent}
          onFocus={selectContent}
          renderOption={renderOption}
        />
      )}
    />
  );
};

export type AddressFieldMultiproviderProps = Omit<AddressFieldProps, 'autocompleteProvider'>;
export const AddressFieldMultiprovider = (p: AddressFieldMultiproviderProps) => {
  const autocompleteProvider = useAddressAutocomplete();
  const renderOption = useCallback(
    (o: IAddressOption) => <ListItemText primary={o.label} sx={{ fontSize: '14px', whiteSpace: 'normal' }} />,
    [],
  );
  return React.createElement(AddressField, { autocompleteProvider, renderOption, ...p });
};

export const AddressFieldLocalProvider = (p: Omit<AddressFieldProps, 'autocompleteProvider'>) => {
  const autocompleteProvider = useLocationAutocomplete();
  const renderOption = useCallback(
    (o: IAddressOption) => <ListItemText primary={o.label} sx={{ fontSize: '14px', whiteSpace: 'normal' }} />,
    [],
  );
  return React.createElement(AddressField, { autocompleteProvider, renderOption, ...p });
};
